import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {ColorSwatchIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {TicketType} from "types/TicketType";
import Dropdown from "components/Dropdown";
import DeleteTicketType from '../Delete';
import TicketingSource from "types/TicketingSource";
import useMoney from "../../../hooks/useMoney";
import {useOperator} from "../../../context/OperatorContext";

function TicketTypeList() {
    const {termFor} = useOperator();
    const {formattedMoney} = useMoney();

    const [page, setPage] = useState<number>(1);
    const result = useQuery(['ticket-types.all', {page}], () => ngage.ticketTypes.all(page), {keepPreviousData: true})

    const [deletingTicketType, setDeletingTicketType] = useState<TicketType | null>(null);

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prefetch next and previous pages
    prefetchNearbyPages('ticket-types.all', page, ngage.ticketTypes.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/ticket-types/new')}>New</Button>

    return (
        <Page title={termFor('Ticket Types')} actions={actions} breadcrumbs={{'Commercial': '/', [termFor('Ticket Types')]: '/ticket-types'}}>
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Description</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>Price</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Tickets')}</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={5}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <ColorSwatchIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No ticket types</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((ticketType, index) => (
                        <TableRow key={ticketType.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position='left'>
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/ticket-types/${ticketType.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/ticket-types/${ticketType.id}/edit`), disabled: ticketType.source.name !== TicketingSource.NGAGE},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingTicketType(ticketType), disabled: (ticketType.source.name !== TicketingSource.NGAGE) || (ticketType.ticket_count > 0)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <ColorSwatchIcon className="h-5 w-5 mr-2 text-gray-500" /> {ticketType.name}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{truncate(ticketType.description, 50)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{ticketType.price? formattedMoney(ticketType.price, ticketType.currency) : 'Free'}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{ticketType.ticket_count?.toLocaleString()}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/ticket-types/${ticketType.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/ticket-types/${ticketType.id}/edit`), disabled: ticketType.source.name !== TicketingSource.NGAGE},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingTicketType(ticketType), disabled: (ticketType.source.name !== TicketingSource.NGAGE) || (ticketType.ticket_count > 0)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteTicketType
                                ticketType={deletingTicketType}
                                show={ticketType === deletingTicketType}
                                onCancel={() => setDeletingTicketType(null)}
                                onDelete={ticketType => {
                                    navigate('/ticket-types')
                                    notify("success", "Ticket Type Deleted", `${ticketType.name} has been permanently removed`)
                                }}
                                onDeleteFail={ticketType => {
                                    setDeletingTicketType(null)
                                    notify("error", "Ticket Type Not Deleted", `${ticketType.name} could not be removed`)
                                }}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default TicketTypeList;